import { Injectable } from '@angular/core';
import { BaseService } from '@services/base.service';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Entity } from 'app/modules/jupiter/types';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { map, Observable } from 'rxjs';
import { Task } from 'app/shared/modules/legacy-tasks/tasks.types';
import { Project, Property } from 'app/modules/projects/projects.types';
import { RelatedToFilter } from './service.types';
import { HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'any' })
export class BaseApiService extends BaseService {
  private tsObject: CustomObject; // this comes only from the jupiter context

  getById<T extends Entity>(id: number): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.get(`${path}/${id}`).pipe(
      map((response) => {
        return response[responseKey];
      }),
    );
  }

  update<T extends Entity>(entity: T): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .patch(`${path}/${entity.id}`, { [responseKey]: entity })
      .pipe(map((response) => response[responseKey]));
  }

  add<T extends Entity>(entity: T): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.post(`${path}`, { [responseKey]: entity }).pipe(map((response) => response[responseKey]));
  }

  delete<T extends Entity>(id: number): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.delete(`${path}/${id}`).pipe(map((response) => response[responseKey]));
  }

  getRelatedDeals(filter: RelatedToFilter): Observable<Project[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;
    const { entityId, fields } = filter;

    const params = new HttpParams({
      fromObject: { page: 1, per_page: 250, sort: 'name', order: 'asc', select: fields?.join(',') },
    });

    return this.httpClient
      .get<{
        count: number;
        next: string;
        offset: number;
        previous: string;
        projects: Project[];
      }>(`${path}/${entityId}/projects`, { params })
      .pipe(map((response) => response.projects));
  }

  getRelatedProperties(filter: RelatedToFilter): Observable<Property[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;
    const { entityId, fields } = filter;

    const params = new HttpParams({
      fromObject: { page: 1, per_page: 250, sort: 'name', order: 'asc', select: fields?.join(',') },
    });

    return this.httpClient
      .get<{
        count: number;
        next: string;
        offset: number;
        previous: string;
        properties: Property[];
      }>(`${path}/${entityId}/properties`, { params })
      .pipe(map((response) => response.properties));
  }

  getRelatedContacts(filter: RelatedToFilter): Observable<Contact[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;
    const { entityId, fields } = filter;

    const params = new HttpParams({
      fromObject: {
        select: fields?.join(','),
      },
    });

    return this.httpClient
      .get<{ contacts: Contact[] }>(`${path}/${entityId}/contacts`, { params })
      .pipe(map(({ contacts }) => contacts));
  }

  getRelatedLoanQuotes(filter: RelatedToFilter): Observable<LoanQuote[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;
    const { entityId, fields } = filter;

    const params = new HttpParams({
      fromObject: { select: [...(fields ?? []), 'project', 'loan_request_id'].join(',') },
    });

    return this.httpClient
      .get<{ loan_quotes: LoanQuote[] }>(`${path}/${entityId}/loan_quotes`, { params })
      .pipe(map(({ loan_quotes }) => loan_quotes));
  }

  getRelatedTasks(entityId: number): Observable<Task[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient.get<{ tasks: Task[] }>(`${path}/${entityId}/tasks`).pipe(map(({ tasks }) => tasks));
  }

  updateAvatar<T>(entityId: number, file: File): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    const uploadData = new FormData();
    uploadData.append('image[attachment]', file);

    return this.httpClient
      .post(`${path}/${entityId}/avatar`, uploadData)
      .pipe(map((response) => response[responseKey]));
  }

  deleteAvatar<T>(entityId: number): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.delete(`${path}/${entityId}/avatar`).pipe(map((response) => response[responseKey]));
  }
}
